import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { FavoritesProvider } from "@/context/FavoritesContext";
import { Layout } from "@/components/Layout";
import { Toaster } from "@/components/ui/sonner";
import { Loader2 } from "lucide-react";

import Home from "@/pages/Home";
import Vehicles from "@/pages/Vehicles";
import VehicleDetail from "@/pages/VehicleDetail";
import Sell from "@/pages/Sell";
import CustomSearch from "@/pages/CustomSearch";
import Import from "@/pages/Import";
import Workshop from "@/pages/Workshop";
import MobileApp from "@/pages/MobileApp";
import Contact from "@/pages/Contact";
import Connexion from "@/pages/Connexion";
import Account from "@/pages/Account";
import VerifierEmail from "@/pages/VerifierEmail";
import AuthCallback from "@/pages/AuthCallback";
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminDashboard from "@/pages/admin/AdminDashboard";

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function Loading() {
  return <div className="min-h-screen flex items-center justify-center bg-[#050505]"><Loader2 className="h-8 w-8 animate-spin text-[#C5A059]" /></div>;
}

function AdminRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <Loading />;
  if (!user || user.role !== "admin") return <Navigate to="/admin/login" replace />;
  return children;
}

function ClientRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <Loading />;
  if (!user) return <Navigate to="/connexion" replace />;
  return children;
}

const withLayout = (el) => <Layout>{el}</Layout>;

function AppRoutes() {
  const location = useLocation();
  // Google OAuth returns to /compte#session_id=... — handle before anything else.
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={withLayout(<Home />)} />
      <Route path="/vehicules" element={withLayout(<Vehicles />)} />
      <Route path="/vehicules/:id" element={withLayout(<VehicleDetail />)} />
      <Route path="/vendre" element={withLayout(<Sell />)} />
      <Route path="/recherche" element={withLayout(<CustomSearch />)} />
      <Route path="/importation" element={withLayout(<Import />)} />
      <Route path="/atelier" element={withLayout(<Workshop />)} />
      <Route path="/application" element={withLayout(<MobileApp />)} />
      <Route path="/contact" element={withLayout(<Contact />)} />
      <Route path="/connexion" element={<Connexion />} />
      <Route path="/verifier-email" element={<VerifierEmail />} />
      <Route path="/compte" element={<ClientRoute>{withLayout(<Account />)}</ClientRoute>} />
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/admin" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <FavoritesProvider>
          <ScrollToTop />
          <AppRoutes />
          <Toaster position="bottom-right" theme="dark" richColors />
        </FavoritesProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
